
import React from 'react';
import { useRoleDashboardStats } from '@/hooks/use-role-dashboard-stats';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertTriangle, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SupportDashboard: React.FC = () => {
  const stats = useRoleDashboardStats('support');
  const navigate = useNavigate();

  if (stats.isLoading) {
    return <div className="p-6">Loading support dashboard...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold">Support Dashboard</h1>
        <p className="text-muted-foreground mt-1">Customer service and issue management</p>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Open Issues</CardTitle>
            <Clock className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{stats.openIssues || 0}</div>
            <p className="text-xs text-muted-foreground">Awaiting resolution</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Critical Issues</CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats.criticalIssues || 0}</div>
            <p className="text-xs text-muted-foreground">High priority</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Resolved Today</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.resolvedToday || 0}</div>
            <p className="text-xs text-muted-foreground">Issues closed</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Problem Procurements</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.problemProcurements || 0}</div>
            <p className="text-xs text-muted-foreground">Rejected items</p>
          </CardContent>
        </Card>
      </div>

      {/* Action Items */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Pending Issues</CardTitle>
            <Button onClick={() => navigate('/alerts')}>View All</Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.pendingIssues?.slice(0, 5).map((issue: any) => (
                <div key={issue.id} className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded">
                  <div>
                    <p className="font-medium">{issue.type}</p>
                    <p className="text-sm text-muted-foreground">{issue.message}</p>
                  </div>
                  <Badge variant={issue.type === 'critical' ? 'destructive' : 'secondary'}>
                    {issue.type}
                  </Badge>
                </div>
              )) || <p className="text-muted-foreground">No pending issues</p>}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Resolutions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.recentResolutions?.map((resolution: any) => (
                <div key={resolution.id} className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{resolution.type}</p>
                    <p className="text-sm text-muted-foreground">{resolution.action_notes || 'Issue resolved'}</p>
                  </div>
                  <Badge variant="default">Resolved</Badge>
                </div>
              )) || <p className="text-muted-foreground">No recent resolutions</p>}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SupportDashboard;
